import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { InputTextarea } from "primereact/inputtextarea";
import { TabPanel, TabView } from "primereact/tabview";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Checkbox } from "primereact/checkbox";
import PageContainer from "../../Components/Page/PageContainer";
import {
  bannerSwapAdminOpsApi,
  type BannerSwapAdminActionResponse,
  type BannerSwapAdminIssue,
} from "../../api/bannerSwapAdminOpsApi";

const kindSeverity = (
  kind: BannerSwapAdminIssue["kind"]
): "danger" | "warning" | "info" => {
  if (kind === "failed_start") return "danger";
  if (kind === "stuck_pending_start") return "warning";
  return "info";
};

const kindLabel = (kind: BannerSwapAdminIssue["kind"]): string => {
  if (kind === "failed_start") return "Failed start";
  if (kind === "stuck_pending_start") return "Stuck pending start";
  return "Stuck pending end";
};

const BannerSwapAdminOps: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [issues, setIssues] = useState<
    Array<BannerSwapAdminIssue & { rowKey: string }>
  >([]);
  const [counts, setCounts] = useState({
    failedStart: 0,
    stuckPendingStart: 0,
    stuckPendingEnd: 0,
    total: 0,
  });
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [selected, setSelected] = useState<
    Array<BannerSwapAdminIssue & { rowKey: string }>
  >([]);
  const [dryRun, setDryRun] = useState(true);
  const [lastResult, setLastResult] =
    useState<BannerSwapAdminActionResponse | null>(null);
  const [pushChannelIdsText, setPushChannelIdsText] = useState("");
  const [stuckSwapUid, setStuckSwapUid] = useState("");
  const [forceSwapUid, setForceSwapUid] = useState("");
  const [forceChannelIdsText, setForceChannelIdsText] = useState("");

  const loadIssues = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bannerSwapAdminOpsApi.getIssues();
      setIssues(
        data.issues.map((issue) => ({
          ...issue,
          rowKey: `${issue.kind}:${issue.swapUID}:${issue.channelId}:${issue.phase}`,
        }))
      );
      setCounts(data.counts);
      setGeneratedAt(data.generatedAt);
      setSelected([]);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ||
        (err instanceof Error ? err.message : "Failed to load issues");
      toast.current?.show({
        severity: "error",
        summary: "Load failed",
        detail: msg,
        life: 6000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadIssues();
  }, [loadIssues]);

  const showResult = (data: BannerSwapAdminActionResponse, title: string) => {
    setLastResult(data);
    const errors = data.rows.filter((r) => r.decision === "error").length;
    toast.current?.show({
      severity: errors > 0 ? "warn" : "success",
      summary: title,
      detail: `processed=${data.processed} dryRun=${data.dryRun} errors=${errors}`,
      life: 7000,
    });
  };

  const selectedTargets = useMemo(
    () =>
      selected.map((s) => ({
        swapUID: s.swapUID,
        channelId: s.channelId,
      })),
    [selected]
  );

  const runReconcile = (reconcileOnly: boolean) => {
    if (selectedTargets.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Select rows",
        detail: "Select one or more start issues first",
        life: 4000,
      });
      return;
    }
    const startOnly = selected.filter((s) => s.phase === "start");
    if (startOnly.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Start rows only",
        detail: "Reconcile/reenqueue applies to start phase rows",
        life: 4000,
      });
      return;
    }
    const label = reconcileOnly
      ? "Reconcile matches only"
      : "Reconcile matches / reenqueue mismatches";
    confirmDialog({
      header: dryRun ? `Dry-run: ${label}` : label,
      message: `${dryRun ? "Preview" : "Apply"} for ${startOnly.length} channel(s). ${
        reconcileOnly
          ? "Fingerprint matches → completed; mismatches skipped."
          : "Fingerprint matches → completed; mismatches → new start Cloud Task."
      }`,
      accept: async () => {
        setActing(true);
        try {
          const data = await bannerSwapAdminOpsApi.reconcile({
            targets: startOnly.map((s) => ({
              swapUID: s.swapUID,
              channelId: s.channelId,
            })),
            dryRun,
            reconcileOnly,
          });
          showResult(data, label);
          if (!dryRun) {
            await loadIssues();
          }
        } catch (err: unknown) {
          const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data
              ?.error ||
            (err instanceof Error ? err.message : "Action failed");
          toast.current?.show({
            severity: "error",
            summary: "Action failed",
            detail: msg,
            life: 6000,
          });
        } finally {
          setActing(false);
        }
      },
    });
  };

  const runStuckRetry = (phase: "start" | "end") => {
    confirmDialog({
      header: dryRun
        ? `Dry-run stuck ${phase} retry`
        : `Retry stuck ${phase} tasks`,
      message: stuckSwapUid.trim()
        ? `Scope: swap ${stuckSwapUid.trim()}`
        : "Scope: all running / partial_failed / scheduled swaps",
      accept: async () => {
        setActing(true);
        try {
          const body = {
            dryRun,
            swapUID: stuckSwapUid.trim() || undefined,
          };
          const data =
            phase === "start"
              ? await bannerSwapAdminOpsApi.retryStuckStarts(body)
              : await bannerSwapAdminOpsApi.retryStuckEnds(body);
          showResult(data, `Stuck ${phase} retry`);
          if (!dryRun) {
            await loadIssues();
          }
        } catch (err: unknown) {
          const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data
              ?.error ||
            (err instanceof Error ? err.message : "Retry failed");
          toast.current?.show({
            severity: "error",
            summary: "Retry failed",
            detail: msg,
            life: 6000,
          });
        } finally {
          setActing(false);
        }
      },
    });
  };

  const runReenqueueSelectedEnds = () => {
    const endOnly = selected.filter((s) => s.phase === "end");
    if (endOnly.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Select end rows",
        detail: "Select one or more stuck pending end issues",
        life: 4000,
      });
      return;
    }
    confirmDialog({
      header: dryRun
        ? "Dry-run reenqueue selected ends"
        : "Reenqueue selected ends",
      message: `${dryRun ? "Preview" : "Bump the end fingerprint and create a new end Cloud Task"} for ${endOnly.length} channel(s). Reconcile on this page only applies to starts.`,
      accept: async () => {
        setActing(true);
        try {
          const data = await bannerSwapAdminOpsApi.forceReenqueueEnds({
            targets: endOnly.map((s) => ({
              swapUID: s.swapUID,
              channelId: s.channelId,
            })),
            dryRun,
          });
          showResult(data, "Reenqueue selected ends");
          if (!dryRun) {
            await loadIssues();
          }
        } catch (err: unknown) {
          const msg =
            (err as { response?: { data?: { error?: string } } })?.response
              ?.data?.error ||
            (err instanceof Error ? err.message : "Reenqueue failed");
          toast.current?.show({
            severity: "error",
            summary: "Reenqueue failed",
            detail: msg,
            life: 6000,
          });
        } finally {
          setActing(false);
        }
      },
    });
  };

  const runForceReenqueueStarts = () => {
    const swapUID = forceSwapUid.trim();
    const channelIds = forceChannelIdsText
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!swapUID || channelIds.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Swap UID + channel IDs required",
        detail: "Enter the swap UID and at least one channel ID",
        life: 4000,
      });
      return;
    }
    const targets = channelIds.map((channelId) => ({ swapUID, channelId }));
    confirmDialog({
      header: dryRun
        ? "Dry-run force re-enqueue starts"
        : "Force re-enqueue starts",
      message: `${dryRun ? "Preview" : "Reset start to pending and enqueue a new start Cloud Task"} for swap #${swapUID} × ${channelIds.length} channel(s). Works even when start is already completed (race wipe / wrong creative live).`,
      acceptClassName: dryRun ? undefined : "p-button-warning",
      accept: async () => {
        setActing(true);
        try {
          const data = await bannerSwapAdminOpsApi.forceReenqueueStarts({
            targets,
            dryRun,
          });
          showResult(data, "Force re-enqueue starts");
        } catch (err: unknown) {
          const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data
              ?.error ||
            (err instanceof Error ? err.message : "Force reenqueue failed");
          toast.current?.show({
            severity: "error",
            summary: "Force reenqueue failed",
            detail: msg,
            life: 6000,
          });
        } finally {
          setActing(false);
        }
      },
    });
  };

  const runForceReenqueueEnds = () => {
    const swapUID = forceSwapUid.trim();
    const channelIds = forceChannelIdsText
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!swapUID || channelIds.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Swap UID + channel IDs required",
        detail: "Enter the swap UID and at least one channel ID",
        life: 4000,
      });
      return;
    }
    const targets = channelIds.map((channelId) => ({ swapUID, channelId }));
    confirmDialog({
      header: dryRun
        ? "Dry-run force re-enqueue ends"
        : "Force re-enqueue ends",
      message: `${dryRun ? "Preview" : "Reset end to pending, bump fingerprint, and enqueue a new end Cloud Task"} for swap #${swapUID} × ${channelIds.length} channel(s).`,
      acceptClassName: dryRun ? undefined : "p-button-warning",
      accept: async () => {
        setActing(true);
        try {
          const data = await bannerSwapAdminOpsApi.forceReenqueueEnds({
            targets,
            dryRun,
          });
          showResult(data, "Force re-enqueue ends");
        } catch (err: unknown) {
          const msg =
            (err as { response?: { data?: { error?: string } } })?.response
              ?.data?.error ||
            (err instanceof Error ? err.message : "Force reenqueue failed");
          toast.current?.show({
            severity: "error",
            summary: "Force reenqueue failed",
            detail: msg,
            life: 6000,
          });
        } finally {
          setActing(false);
        }
      },
    });
  };

  const runPushDefaults = () => {
    const channelIds = pushChannelIdsText
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (channelIds.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Channel IDs required",
        detail: "Paste one or more YouTube channel IDs",
        life: 4000,
      });
      return;
    }
    confirmDialog({
      header: dryRun ? "Dry-run push defaults" : "Push default banners",
      message: `${dryRun ? "Preview" : "Force-upload DefaultBannerURL to YouTube"} for ${channelIds.length} channel(s). This ignores brand-boost windows.`,
      acceptClassName: dryRun ? undefined : "p-button-danger",
      accept: async () => {
        setActing(true);
        try {
          const data = await bannerSwapAdminOpsApi.pushDefaults({
            channelIds,
            dryRun,
          });
          showResult(data, "Push defaults");
        } catch (err: unknown) {
          const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data
              ?.error ||
            (err instanceof Error ? err.message : "Push failed");
          toast.current?.show({
            severity: "error",
            summary: "Push failed",
            detail: msg,
            life: 6000,
          });
        } finally {
          setActing(false);
        }
      },
    });
  };

  return (
    <PageContainer>
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="p-3">
        <div className="flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
          <div>
            <h2 className="m-0">Banner Swap Admin Ops</h2>
            <p className="text-color-secondary m-0 mt-1">
              Admin-only recovery for false failures, stuck pending starts/ends,
              force-restarting completed starts (race wipe), and pushing default
              banners live.
            </p>
          </div>
          <div className="flex align-items-center gap-3">
            <div className="flex align-items-center gap-2">
              <Checkbox
                inputId="dryRun"
                checked={dryRun}
                onChange={(e) => setDryRun(Boolean(e.checked))}
              />
              <label htmlFor="dryRun">Dry-run (preview only)</label>
              {dryRun ? (
                <Tag
                  severity="warning"
                  value="No Cloud Tasks will be created until you uncheck this"
                />
              ) : null}
            </div>
            <Button
              label="Refresh issues"
              icon="pi pi-refresh"
              loading={loading}
              onClick={() => void loadIssues()}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <Tag
            severity="danger"
            value={`Failed starts: ${counts.failedStart}`}
          />
          <Tag
            severity="warning"
            value={`Stuck pending starts: ${counts.stuckPendingStart}`}
          />
          <Tag
            severity="info"
            value={`Stuck pending ends: ${counts.stuckPendingEnd}`}
          />
          <Tag value={`Total: ${counts.total}`} />
          {generatedAt ? (
            <span className="text-sm text-color-secondary align-self-center">
              Scanned {new Date(generatedAt).toLocaleString()}
            </span>
          ) : null}
        </div>

        <TabView>
          <TabPanel header="Issues">
            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                label="Reconcile matches only"
                icon="pi pi-check"
                disabled={acting || selected.length === 0}
                onClick={() => runReconcile(true)}
              />
              <Button
                label="Reconcile or reenqueue"
                icon="pi pi-replay"
                severity="warning"
                disabled={acting || selected.length === 0}
                onClick={() => runReconcile(false)}
              />
              <Button
                label="Reenqueue selected ends"
                icon="pi pi-undo"
                severity="info"
                disabled={acting || selected.length === 0}
                onClick={runReenqueueSelectedEnds}
              />
            </div>
            <DataTable
              value={issues}
              loading={loading}
              selection={selected}
              onSelectionChange={(e) =>
                setSelected(
                  (e.value as Array<BannerSwapAdminIssue & { rowKey: string }>) ??
                    []
                )
              }
              dataKey="rowKey"
              paginator
              rows={25}
              rowsPerPageOptions={[10, 25, 50]}
              emptyMessage="No ops issues found"
              size="small"
              selectionMode="checkbox"
            >
              <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
              <Column
                field="kind"
                header="Kind"
                body={(row: BannerSwapAdminIssue & { rowKey: string }) => (
                  <Tag
                    severity={kindSeverity(row.kind)}
                    value={kindLabel(row.kind)}
                  />
                )}
                sortable
              />
              <Column
                field="swapUID"
                header="Swap UID"
                sortable
                style={{ width: "7rem" }}
                body={(row: BannerSwapAdminIssue & { rowKey: string }) => (
                  <button
                    type="button"
                    className="font-mono text-sm p-0 border-0 bg-transparent cursor-pointer hover:underline"
                    title="Copy swap UID"
                    onClick={() => {
                      void navigator.clipboard?.writeText(String(row.swapUID));
                    }}
                  >
                    #{row.swapUID}
                  </button>
                )}
              />
              <Column
                field="channelTitle"
                header="Channel"
                body={(row: BannerSwapAdminIssue & { rowKey: string }) => (
                  <div>
                    <div>{row.channelTitle || "—"}</div>
                    <div className="text-xs text-color-secondary">
                      {row.channelId}
                    </div>
                  </div>
                )}
              />
              <Column field="rowStatus" header="Status" sortable />
              <Column field="attemptCount" header="Attempts" sortable />
              <Column
                field="lastError"
                header="Last error"
                body={(row: BannerSwapAdminIssue & { rowKey: string }) =>
                  row.lastError ? (
                    <span title={row.lastError}>
                      {row.lastError.slice(0, 80)}
                      {row.lastError.length > 80 ? "…" : ""}
                    </span>
                  ) : (
                    "—"
                  )
                }
              />
              <Column
                field="effectiveStartAt"
                header="Start"
                body={(row: BannerSwapAdminIssue & { rowKey: string }) =>
                  row.effectiveStartAt
                    ? new Date(row.effectiveStartAt).toLocaleString()
                    : "—"
                }
              />
              <Column
                field="effectiveEndAt"
                header="End"
                body={(row: BannerSwapAdminIssue & { rowKey: string }) =>
                  row.effectiveEndAt
                    ? new Date(row.effectiveEndAt).toLocaleString()
                    : "—"
                }
              />
            </DataTable>
          </TabPanel>

          <TabPanel header="Stuck batch retry">
            <p className="text-color-secondary">
              Re-enqueue Cloud Tasks for channels stuck at pending/attempt 0 past
              their start or end date. End retry now bumps the schedule
              fingerprint so a <em>new</em> Cloud Task is created (reusing the
              original task ID is a no-op). Uncheck Dry-run before applying.
            </p>
            <div className="flex flex-column gap-3" style={{ maxWidth: 480 }}>
              <div>
                <label className="block mb-2" htmlFor="stuckSwapUid">
                  Optional swap UID filter
                </label>
                <input
                  id="stuckSwapUid"
                  className="p-inputtext p-component w-full"
                  value={stuckSwapUid}
                  onChange={(e) => setStuckSwapUid(e.target.value)}
                  placeholder="e.g. 555"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  label="Retry stuck starts"
                  icon="pi pi-play"
                  loading={acting}
                  onClick={() => runStuckRetry("start")}
                />
                <Button
                  label="Retry stuck ends"
                  icon="pi pi-undo"
                  severity="secondary"
                  loading={acting}
                  onClick={() => runStuckRetry("end")}
                />
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Force re-enqueue">
            <p className="text-color-secondary">
              Hard-restart start or end even when status looks terminal. Start:
              use for race wipes. End: use when revert never ran (including
              overdue swaps whose end Cloud Task was never created because the
              date was beyond the 29-day horizon). Resets the row to pending,
              bumps the schedule fingerprint, and enqueues a new Cloud Task.
            </p>
            <div className="flex flex-column gap-3" style={{ maxWidth: 520 }}>
              <div>
                <label className="block mb-2" htmlFor="forceSwapUid">
                  Swap UID
                </label>
                <input
                  id="forceSwapUid"
                  className="p-inputtext p-component w-full"
                  value={forceSwapUid}
                  onChange={(e) => setForceSwapUid(e.target.value)}
                  placeholder="e.g. 569"
                />
              </div>
              <div>
                <label className="block mb-2" htmlFor="forceChannelIds">
                  Channel IDs (comma or newline separated)
                </label>
                <InputTextarea
                  id="forceChannelIds"
                  className="w-full"
                  rows={4}
                  value={forceChannelIdsText}
                  onChange={(e) => setForceChannelIdsText(e.target.value)}
                  placeholder="UCPm7RQkpEemhfEVdIlzqPRg"
                />
              </div>
              <Button
                label={
                  dryRun
                    ? "Dry-run force re-enqueue start"
                    : "Force re-enqueue start now"
                }
                icon="pi pi-replay"
                severity={dryRun ? "secondary" : "warning"}
                loading={acting}
                onClick={runForceReenqueueStarts}
              />
              <Button
                label={
                  dryRun
                    ? "Dry-run force re-enqueue end"
                    : "Force re-enqueue end now"
                }
                icon="pi pi-undo"
                severity={dryRun ? "secondary" : "warning"}
                loading={acting}
                onClick={runForceReenqueueEnds}
              />
            </div>
          </TabPanel>

          <TabPanel header="Push default banners">
            <p className="text-color-secondary">
              Force-upload each channel&apos;s stored{" "}
              <code>DefaultBannerURL</code> to YouTube. Use when a swap left the
              wrong creative live and you need the default restored immediately.
              Ignores brand-boost deferral.
            </p>
            <label className="block mb-2" htmlFor="pushIds">
              Channel IDs (comma or newline separated)
            </label>
            <InputTextarea
              id="pushIds"
              className="w-full mb-3"
              rows={6}
              value={pushChannelIdsText}
              onChange={(e) => setPushChannelIdsText(e.target.value)}
              placeholder="UC-S_59FJ3GZqev6X_VJcqGg"
            />
            <Button
              label={dryRun ? "Dry-run push defaults" : "Push defaults now"}
              icon="pi pi-upload"
              severity={dryRun ? "secondary" : "danger"}
              loading={acting}
              onClick={runPushDefaults}
            />
          </TabPanel>
        </TabView>

        {lastResult ? (
          <div className="mt-4">
            <h3 className="mt-0">Last action result</h3>
            <DataTable
              value={lastResult.rows}
              size="small"
              paginator
              rows={20}
              emptyMessage="No rows"
            >
              <Column field="swapUID" header="Swap" />
              <Column field="channelId" header="Channel" />
              <Column field="decision" header="Decision" />
              <Column field="reason" header="Reason" />
              <Column field="enqueued" header="Enqueued" />
              <Column field="error" header="Error" />
            </DataTable>
          </div>
        ) : null}
      </div>
    </PageContainer>
  );
};

export default BannerSwapAdminOps;
